import { useMemo } from 'react';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { type ProductListResponse } from '@/api/generated';
import { productsV3GetProductsOptions } from '@/api/generated/@tanstack/react-query.gen';
import { parseCarrierType } from '@/types/insurance.type';
import { type PageRequirements, type Product, isProductInPage } from '@/types/product.type.ts';

function useGetProductCatalogQuery() {
  return useQuery({
    ...productsV3GetProductsOptions({ query: { catalog: 'D2C', state: 'NY', limit: 9999 } }),
    select: transformToProducts,
  });
}

const transformToProducts = (data: ProductListResponse): Product[] => {
  return data.payload.map(
    (item): Product => ({
      uuid: item.uuid,
      name: item.name,
      description: item.description ?? null,
      image: item.image ?? '',
      category: item.category ?? { uuid: '', name: '' },
      eligibility: item.eligibility.map((elig) => ({
        type: parseCarrierType(elig.type),
        label: elig.label,
        templateUuid: elig.template_uuid,
      })),
    })
  );
};

type QueryWithProducts = UseQueryResult<Product[]> & { products: Product[] };

function useGetPageProducts(pageRequirements: PageRequirements): QueryWithProducts {
  const query = useGetProductCatalogQuery();
  const products = useMemo(
    () => query.data?.filter((product) => isProductInPage(product, pageRequirements)) ?? [],
    [query.data, pageRequirements]
  );
  return { ...query, products };
}

export { useGetProductCatalogQuery, useGetPageProducts };
